import type { z } from "zod";
import type * as S from "@shipyard/shared";
import i18n from "@/i18n";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

const BASE = "/api/v1";

const languageHeader = () => ({ "Accept-Language": i18n.language });

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { ...languageHeader(), "Content-Type": "application/json" } : languageHeader(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? "error", data?.message ?? res.statusText, data?.details);
  }
  return data as T;
}

export const http = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export async function authPost<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api/auth${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { ...languageHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data?.code ?? "error", data?.message ?? res.statusText);
  return data as T;
}

/** Upload with progress reporting (fetch has no upload progress events). */
export function uploadBuild(
  appId: string,
  file: File,
  fields: Record<string, string>,
  onProgress: (fraction: number) => void,
): { promise: Promise<S.Build>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<S.Build>((resolve, reject) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v) form.append(k, v);
    form.append("file", file);
    xhr.open("POST", `${BASE}/apps/${appId}/builds`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiError(xhr.status, data?.error ?? "error", data?.message ?? xhr.statusText, data?.details));
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error", "Network error"));
    xhr.onabort = () => reject(new ApiError(0, "aborted", "Upload cancelled"));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

export type Input<T extends z.ZodType> = z.input<T>;
export type { S };
